import { Ban, CheckCircle2, RotateCcw, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EditRoleModal } from '../components/EditRoleModal';
import { RolePermissionsModal } from '../components/RolePermissionsModal';
import { rolesApi } from '../rolesApi';
import type { RoleListItem } from '../roleTypes';
import { usePermissionCatalog } from '../usePermissionCatalog';
import { useRoleProfile } from '../useRoleProfile';
import {
  roleDeleteDisabledReason,
  roleEditDisabledReason,
  rolePermissionsDisabledReason,
} from '../utils/roleState';

type TabKey = 'info' | 'permissions';

/**
 * The role profile — what a role is, and exactly what it grants.
 *
 * On the **header** layout rather than the sidebar one: a role reads as a single continuous
 * thing — a name, a handful of facts, and one long list of permissions — rather than a record
 * whose content splits into panels a reader chooses between.
 *
 * Its panels are the create form's two steps: **Info** is what the role IS, **Permissions** is
 * what it GRANTS. Each has its own edit under its own key — `roles.update` in the header cog,
 * `roles.assign-permissions` on the Permissions card — because the server writes them through
 * two endpoints and a reader may hold one key without the other.
 */
export function RoleProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { role, error, reload } = useRoleProfile(id);
  const { catalog, loading: catalogLoading } = usePermissionCatalog();

  // The member count is ACCOUNT data — the list gates its column on the same key.
  const canReadUsers = usePermission('users.read');

  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState(false);
  const [permissionsFor, setPermissionsFor] = useState<RoleListItem | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pending, setPending] = useState<'delete' | 'restore' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  // A plain string is a segment that goes nowhere; only Roles is a link back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Administration',
    { label: 'Roles', to: '/roles' },
    role?.name ?? 'Role',
  ];

  // A failed REFRESH keeps the page; only a role that never loaded is replaced by the error.
  if (error && !role) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={reload}
        />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  async function runPending() {
    if (!role || !pending) return;

    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await rolesApi.deactivate(role.id);
      else await rolesApi.activate(role.id);

      setPending(null);
      reload();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="role-profile"
        header={
          <ProfileHeader
            id="role-profile-header"
            icon={<ShieldCheck className="h-6 w-6" aria-hidden="true" />}
            title={role.name}
            meta={[
              role.description,
              /*
                Counts of what the role reaches, unlabelled — the header's meta line is for
                facts that need no label, and "3 members" says what it is on its own.
              */
              canReadUsers ? `${role.userCount} ${role.userCount === 1 ? 'member' : 'members'}` : '',
              role.isSystem ? 'System role' : '',
            ]}
            status={
              <StatusText tone={role.isActive ? 'active' : 'inactive'}>
                {role.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={role.createdByName}
            createdAt={role.createdAt}
            /*
              The record's own cog. Edit is PROMOTED here from the Details card rather than
              offered twice — it is the same modal over the same two fields. History is scoped
              by id alone, so a refused write against this role still shows.
            */
            settings={{
              onEdit: () => setEditing(true),
              editPermission: 'roles.update',
              editDisabledReason: roleEditDisabledReason(role),
              onHistory: () => setHistoryOpen(true),
              onDelete: () => {
                setActionError(null);
                setPending('delete');
              },
              deletePermission: 'roles.delete',
              deleteDisabledReason: roleDeleteDisabledReason(role),
            }}
          />
        }
        banner={
          role.isActive ? (
            <ProfileBanner
              id="role-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                role.isSystem
                  ? 'This is a system role. It is granted every permission on boot and cannot be deleted.'
                  : 'This role is active. Accounts assigned to it hold the permissions it grants.'
              }
            />
          ) : (
            <ProfileBanner
              id="role-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This role is deleted. It grants nothing until it is restored."
              action={
                <PermissionGate permission="roles.delete">
                  <Button
                    id="role-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => {
                      setActionError(null);
                      setPending('restore');
                    }}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'permissions',
            label: (
              <>
                Permissions
                <CountBadge count={role.permissionKeys.length} tone="slate" />
              </>
            ),
            /*
              The panel renders the permission catalog, which is the same endpoint the role
              editor reads. A caller without the key would open the tab onto an error state.
            */
            permission: 'roles.read',
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            {/* No cog: its one entry, Edit, is the header's — promoted, never duplicated. */}
            <ProfileCard id="role-profile-details" title="Details">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Name', value: role.name },
                  { label: 'Description', value: role.description },
                  { label: 'Type', value: role.isSystem ? 'System' : 'Custom' },
                  // The guarded row renders NOTHING without the key, not an em dash.
                  ...(canReadUsers
                    ? [
                        {
                          label: 'Members',
                          value: <span className="font-mono tabular-nums">{role.userCount}</span>,
                        },
                      ]
                    : []),
                  {
                    label: 'Permissions',
                    value: (
                      <span className="font-mono tabular-nums">{role.permissionKeys.length}</span>
                    ),
                  },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable — no cog, at any permission level. */}
            <ProfileCard id="role-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Created by',
                    value: role.createdByName,
                  },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(role.createdAt)} className="font-mono">
                        {formatDateTime(role.createdAt)}
                      </time>
                    ),
                  },
                  {
                    label: 'Updated by',
                    value: role.updatedByName,
                  },
                  {
                    label: 'Latest update',
                    value: role.updatedAt ? (
                      <time dateTime={toIso(role.updatedAt)} className="font-mono">
                        {formatDateTime(role.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'permissions' ? (
          <ProfileCard
            padded
            id="role-profile-permissions"
            title="Granted permissions"
            count={role.permissionKeys.length}
            description="What every account assigned this role can do, unless its own grants were trimmed when it was created."
            // The grid's own key, not `roles.update` — what a role GRANTS is a separate write.
            onEdit={() =>
              setPermissionsFor({ ...role, permissionCount: role.permissionKeys.length })
            }
            editPermission="roles.assign-permissions"
            editDisabledReason={rolePermissionsDisabledReason(role)}
          >
            {catalogLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              /*
                Read-only, and opened on the modules this role actually grants — the same
                treatment the user profile gives an account's effective set. The card's Edit
                opens the editor.
              */
              <PermissionPicker
                idPrefix="role-profile-perm"
                catalog={catalog}
                value={role.permissionKeys}
                disabled
                showOnlyEnabled
                onChange={() => undefined}
                emptyMessage="No permissions are registered yet."
              />
            )}
          </ProfileCard>
        ) : null}
      </ProfileHeaderShell>

      {editing ? (
        <EditRoleModal
          role={role}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}

      <RolePermissionsModal
        role={permissionsFor}
        onClose={() => setPermissionsFor(null)}
        onSaved={() => {
          setPermissionsFor(null);
          reload();
        }}
      />

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="roles"
        entityId={role.id}
      />

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runPending()}
          title={pending === 'delete' ? 'Delete role' : 'Restore role'}
          danger={pending === 'delete'}
          // The tick on both — a banner action WRITES. The password on the destructive half only.
          requireConfirm
          requireCredential={pending === 'delete'}
          loading={running}
          id={`role-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'delete'
                  ? `"${role.name}" will stop granting its permissions. Nothing is erased — it can be restored later.`
                  : `"${role.name}" will grant its permissions again to any account assigned it.`}
              </p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
